// Pure snapshot model for the Phoebe chat debug panels: timeline assembly,
// token-usage summarization, and payload readers. No React/JSX here — view
// components live in phoebe_chat_debug_event_timeline.tsx and
// phoebe_chat_debug_sidebar.tsx.
import { z } from 'zod';

import {
    type PhoebeAgentRunEventStreamConnectionStatus,
    type PhoebeAgentRunStateRead,
} from '@apps/web/hooks/use_phoebe_agent_run_event_stream';
import { type components } from '@libraries/typescript/api_client';

type PhoebeAgentRunEventRead = components['schemas']['PhoebeAgentRunEventRead'];
type PhoebeAgentRunPendingEventRead =
    components['schemas']['PhoebeAgentRunPendingEventRead'];
export type PhoebeChatDebugLinkedResource = NonNullable<
    PhoebeAgentRunStateRead['resources']
>[number];

const argumentsSchema = z.record(z.string(), z.json());
const eventPayloadSchema = z.object({
    call_id: z.string().optional(),
    tool_call_id: z.string().optional(),
    tool_name: z.string().optional(),
    arguments_json: argumentsSchema.optional(),
    text: z.string().optional(),
    reasoning: z.string().optional(),
    label: z.string().optional(),
    source: z.string().optional(),
    detail: z.string().optional(),
    description: z.string().optional(),
    output_json: z.json().nullish(),
    output_text: z.string().nullish(),
    result: z.json().nullish(),
    result_json: z.json().nullish(),
    error: z.string().nullish(),
});
const commandSchema = z.object({
    command: z.string().optional(),
    description: z.string().optional(),
});

type PhoebeChatDebugEventContent = {
    readonly callId: string | undefined;
    readonly toolName: string | undefined;
    readonly argumentsJson: z.infer<typeof argumentsSchema>;
    readonly command: string | undefined;
    readonly description: string | undefined;
    readonly prose: string | undefined;
    readonly label: string | undefined;
    readonly source: string | undefined;
    readonly detail: string | undefined;
    readonly output: z.infer<ReturnType<typeof z.json>> | undefined;
    readonly error: string | undefined;
};

/** Interpret event payloads once for both the raw timeline and transcript. */
export function readTimelineEntryContent(
    raw: PhoebeChatDebugTimelineEntry['raw'],
): PhoebeChatDebugEventContent {
    const parsed = eventPayloadSchema.safeParse(raw['payload']);
    const payload = parsed.success ? parsed.data : {};
    const argumentsJson = payload.arguments_json ?? {};
    const parsedCommand = commandSchema.safeParse(argumentsJson);
    const command = parsedCommand.success ? parsedCommand.data : {};
    // Canonical plain-text tool events retain output_json={} for legacy readers.
    const outputJson =
        isRecord(payload.output_json) &&
        Object.keys(payload.output_json).length === 0
            ? (payload.output_text ?? payload.output_json)
            : payload.output_json;
    return {
        callId: payload.call_id ?? payload.tool_call_id,
        toolName: payload.tool_name,
        argumentsJson,
        command: command.command,
        description: command.description ?? payload.description,
        prose: payload.text ?? payload.reasoning ?? payload.detail,
        label: payload.label,
        source: payload.source,
        detail: payload.detail,
        output:
            outputJson ??
            payload.output_text ??
            payload.result ??
            payload.result_json ??
            undefined,
        error: payload.error ?? undefined,
    };
}

export type PhoebeChatDebugTimelineEntry = {
    readonly id: string;
    readonly source: 'event' | 'pending';
    readonly type: string;
    readonly createdAt: string;
    readonly eventOffset: number | null;
    readonly status: string | null;
    readonly raw: Record<string, unknown>;
    readonly content: PhoebeChatDebugEventContent;
};

export type PhoebeChatDebugTokenUsage = {
    readonly model: string | null;
    readonly reportedInputTokens: number | null;
    readonly reportedOutputTokens: number | null;
    readonly reportedTotalTokens: number | null;
    readonly contextInputTokens: number | null;
    readonly estimatedPayloadTokens: number;
    readonly contextWindowTokens: number | null;
    readonly contextUsagePercent: number | null;
};

export type PhoebeChatAgentGeneration = 'v2' | 'v3' | null;

export type PhoebeChatDebugSnapshot = {
    readonly conversationId: string | null;
    readonly conversationTitle: string;
    readonly phoebeRunId: string | null;
    readonly agentGeneration: PhoebeChatAgentGeneration;
    readonly eventReadEnabled: boolean;
    readonly connectionStatus: PhoebeAgentRunEventStreamConnectionStatus;
    readonly runState: PhoebeAgentRunStateRead | null;
    readonly timeline: Array<PhoebeChatDebugTimelineEntry>;
    readonly linkedResources: Array<PhoebeChatDebugLinkedResource>;
    readonly tokenUsage: PhoebeChatDebugTokenUsage;
};

export function buildPhoebeChatDebugSnapshot({
    conversationId,
    conversationTitle,
    phoebeRunId,
    agentGeneration,
    eventReadEnabled,
    connectionStatus,
    runState,
    events,
    pendingEvents,
}: {
    readonly conversationId: string | null;
    readonly conversationTitle: string;
    readonly phoebeRunId: string | null;
    readonly agentGeneration: PhoebeChatAgentGeneration;
    readonly eventReadEnabled: boolean;
    readonly connectionStatus: PhoebeAgentRunEventStreamConnectionStatus;
    readonly runState: PhoebeAgentRunStateRead | null;
    readonly events: Array<PhoebeAgentRunEventRead>;
    readonly pendingEvents: Array<PhoebeAgentRunPendingEventRead>;
}): PhoebeChatDebugSnapshot {
    const committedTimeline = events.map((event) => ({
        id: event.id,
        source: 'event' as const,
        type: String(event.type),
        createdAt: event.created_at,
        eventOffset: event.event_offset,
        status: null,
        raw: { ...event },
        content: readTimelineEntryContent(event),
    }));
    const pendingTimeline = pendingEvents.map((event) => ({
        id: event.id,
        source: 'pending' as const,
        type: String(event.type),
        createdAt: event.created_at,
        eventOffset: null,
        status: String(event.status),
        raw: { ...event },
        content: readTimelineEntryContent(event),
    }));
    const toolCallsById = new Map<string, PhoebeChatDebugEventContent>();
    const toolOutputsById = new Map<string, PhoebeChatDebugEventContent>();
    for (const entry of committedTimeline) {
        if (!entry.content.callId) {
            continue;
        }
        if (entry.type === 'tool_call') {
            toolCallsById.set(entry.content.callId, entry.content);
        } else if (entry.type === 'tool_output') {
            toolOutputsById.set(entry.content.callId, entry.content);
        }
    }
    const timeline = [...committedTimeline, ...pendingTimeline]
        .map((entry) => {
            const content = entry.content;
            const call = content.callId
                ? toolCallsById.get(content.callId)
                : undefined;
            const output = content.callId
                ? toolOutputsById.get(content.callId)
                : undefined;
            return {
                ...entry,
                content: {
                    ...content,
                    toolName: call?.toolName ?? content.toolName,
                    argumentsJson: call?.argumentsJson ?? content.argumentsJson,
                    command: call?.command ?? content.command,
                    description: call?.description ?? content.description,
                    output: content.output ?? output?.output,
                    error: content.error ?? output?.error,
                },
            };
        })
        .sort(compareTimelineEntriesAscending);

    return {
        conversationId,
        conversationTitle,
        phoebeRunId,
        agentGeneration,
        eventReadEnabled,
        connectionStatus,
        runState,
        timeline,
        linkedResources: runState?.resources ?? [],
        tokenUsage: summarizeTokenUsage(timeline),
    };
}

function compareTimelineEntriesAscending(
    left: PhoebeChatDebugTimelineEntry,
    right: PhoebeChatDebugTimelineEntry,
): number {
    if (left.eventOffset !== null && right.eventOffset !== null) {
        const offsetCompare = left.eventOffset - right.eventOffset;
        if (offsetCompare !== 0) {
            return offsetCompare;
        }
    }

    const timeCompare = left.createdAt.localeCompare(right.createdAt);
    if (timeCompare !== 0) {
        return timeCompare;
    }

    return left.id.localeCompare(right.id);
}

function summarizeTokenUsage(
    timeline: Array<PhoebeChatDebugTimelineEntry>,
): PhoebeChatDebugTokenUsage {
    let model: string | null = null;
    let reportedInputTokens: number | null = null;
    let reportedOutputTokens: number | null = null;
    let reportedTotalTokens: number | null = null;
    let contextInputTokens: number | null = null;
    let contextWindowTokens: number | null = null;
    let estimatedPayloadTokens = 0;

    for (const entry of timeline) {
        estimatedPayloadTokens += estimateTokenCount(entry.raw['payload']);

        const payload = entry.raw['payload'];
        if (!isRecord(payload)) {
            continue;
        }

        const payloadModel = payload['model'];
        if (typeof payloadModel === 'string' && payloadModel) {
            model = payloadModel;
        }

        const usage = getUsageRecord(payload);
        if (usage) {
            const usageModel = usage['model'];
            if (typeof usageModel === 'string' && usageModel) {
                model = usageModel;
            }
            reportedInputTokens =
                readFirstNumber(usage, [
                    'input_tokens',
                    'prompt_tokens',
                    'prompt_token_count',
                ]) ?? reportedInputTokens;
            reportedOutputTokens =
                readFirstNumber(usage, [
                    'output_tokens',
                    'completion_tokens',
                    'candidates_token_count',
                ]) ?? reportedOutputTokens;
            reportedTotalTokens =
                readFirstNumber(usage, ['total_tokens', 'total_token_count']) ??
                reportedTotalTokens;
            contextInputTokens =
                readFirstNumber(usage, [
                    'context_input_tokens',
                    'max_context_input_tokens',
                    'effective_input_tokens',
                ]) ?? contextInputTokens;
            contextWindowTokens =
                readFirstNumber(usage, [
                    'context_window_tokens',
                    'context_window',
                    'max_context_tokens',
                    'model_context_window',
                ]) ?? contextWindowTokens;
        }

        contextWindowTokens =
            readFirstNumber(payload, [
                'context_window_tokens',
                'context_window',
                'max_context_tokens',
                'model_context_window',
                'budget_tokens',
            ]) ?? contextWindowTokens;
    }

    if (
        reportedTotalTokens === null &&
        (reportedInputTokens !== null || reportedOutputTokens !== null)
    ) {
        reportedTotalTokens =
            (reportedInputTokens ?? 0) + (reportedOutputTokens ?? 0);
    }

    const contextUsageBasisTokens =
        contextInputTokens ?? reportedInputTokens ?? estimatedPayloadTokens;
    const contextUsagePercent =
        contextWindowTokens !== null && contextWindowTokens > 0
            ? (contextUsageBasisTokens / contextWindowTokens) * 100
            : null;

    return {
        model,
        reportedInputTokens,
        reportedOutputTokens,
        reportedTotalTokens,
        contextInputTokens,
        estimatedPayloadTokens,
        contextWindowTokens,
        contextUsagePercent,
    };
}

function getUsageRecord(
    payload: Record<string, unknown>,
): Record<string, unknown> | null {
    const usage = payload['usage'];
    if (isRecord(usage)) {
        return usage;
    }

    const tokenUsage = payload['token_usage'];
    return isRecord(tokenUsage) ? tokenUsage : null;
}

function readFirstNumber(
    record: Record<string, unknown>,
    keys: Array<string>,
): number | null {
    for (const key of keys) {
        const value = record[key];
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }
    }
    return null;
}

function estimateTokenCount(value: unknown): number {
    if (value === undefined || value === null) {
        return 0;
    }
    const serialized =
        typeof value === 'string' ? value : JSON.stringify(value) || '';
    return Math.ceil(serialized.length / 4);
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function getCompactionSummaryText(
    entry: PhoebeChatDebugTimelineEntry,
): string | null {
    if (entry.type !== 'item_compaction') {
        return null;
    }
    const payload = entry.raw['payload'];
    if (!isRecord(payload)) {
        return null;
    }
    const summaryText = payload['summary_text'];
    return typeof summaryText === 'string' && summaryText.trim()
        ? summaryText.trim()
        : null;
}

// tool_call / tool_output payloads carry the tool name; surface it on the
// collapsed row so entries are identifiable without expanding them.
export function getTimelineEntryToolName(
    entry: PhoebeChatDebugTimelineEntry,
): string | null {
    return entry.content.toolName || null;
}

export function getTimelineEntryCommand(
    entry: PhoebeChatDebugTimelineEntry,
): string | null {
    return entry.content.command || null;
}

export function getTimelineEntryLabel(
    entry: PhoebeChatDebugTimelineEntry,
): string {
    if (entry.type !== 'status_update') {
        return entry.type;
    }
    const { label, source } = entry.content;
    if (source === 'sandbox' && label) {
        return `sandbox: ${label}`;
    }
    return label || entry.type;
}

export function shortId(value: string | null): string {
    if (!value) {
        return 'none';
    }
    return value.length > 8 ? value.slice(0, 8) : value;
}
